import { useEffect, useState } from 'react';
import { AnnonymousUser } from '../interfaces';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../store/store';
import { resetGoToReset, setEmail, signup } from '../store/authSlice';
import loadingGif from '/asset/loading.gif';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'react-toastify';
import AuthCard, {
  authButtonClass,
  authInputClass,
  authLabelClass,
} from '../components/AuthCard';

export default function Signup() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+234[1-9]\d{9}$/;

  const { status: auth_status, gotoResetPw } = useSelector(
    (state: RootState) => state.auth
  );
  const { routesPrefix } = useSelector((state: RootState) => state.setup);
  const [phone, setPhone] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [annonymousUser, setAnnonymousUser] = useState<AnnonymousUser>({
    emailAddress: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  const [annonymousUserValidDetails, setAnnonymousUserValidDetails] =
    useState<AnnonymousUser>({
      emailAddress: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
    });

  const [validDetailsState, setValidDetailsState] = useState(false);

  useEffect(() => {
    const anonValid = { ...annonymousUserValidDetails };
    let valid = true;
    if (emailRegex.test(annonymousUser.emailAddress)) {
      anonValid.emailAddress = '';
      valid = valid && true;
    } else {
      anonValid.emailAddress = 'Enter a valid email address';
      valid = valid && false;
    }

    if (annonymousUser.firstName.length > 2) {
      anonValid.firstName = '';
      valid = valid && true;
    } else {
      anonValid.firstName = 'Enter a valid name';
      valid = valid && false;
    }

    if (annonymousUser.lastName.length > 2) {
      anonValid.lastName = '';
      valid = valid && true;
    } else {
      anonValid.lastName = 'Enter a valid name';
      valid = valid && false;
    }

    if (phoneRegex.test(annonymousUser.phoneNumber)) {
      anonValid.phoneNumber = '';
      valid = valid && true;
    } else {
      anonValid.phoneNumber = 'Enter a valid phone number';
      valid = valid && false;
    }

    setAnnonymousUserValidDetails(anonValid);
    setValidDetailsState(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annonymousUser]);

  const updateAnnonymousUser = (name: string, value: string) => {
    setAnnonymousUser({ ...annonymousUser, [name]: value });
  };

  useEffect(() => {
    updateAnnonymousUser('phoneNumber', phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  useEffect(() => {
    dispatch(resetGoToReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gotoResetPw) {
      navigate(`${routesPrefix}setPassword`);
    }
  }, [gotoResetPw, routesPrefix, navigate]);

  const handleSignup = () => {
    if (!validDetailsState) {
      toast.error('Please fill all fields correctly');
      return;
    }
    dispatch(signup(annonymousUser)).then((res) => {
      if (res?.payload) {
        if (res?.payload?.succeded) {
          if (res?.payload?.message) {
            toast.success(res?.payload?.message);
          }
          toast.success('Check your email for token for password creation');
          navigate(`${routesPrefix}setpassword`);
        } else {
          if (res?.payload?.message) {
            toast.error(res?.payload?.message);
          } else {
            toast.error('Error signing up the user');
          }
        }
      }
    });
  };

  const fieldError = (message: string) =>
    message !== '' && (
      <p className="text-[12px] text-red-500 mt-[6px] font-dmsans">{message}</p>
    );

  return (
    <AuthCard
      title="Create your account"
      subtitle="Join us and start shopping today"
    >
      {/* First & Last Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[22px] sm:gap-[16px] mb-[22px]">
        <div>
          <label className={authLabelClass}>First Name</label>
          <input
            className={authInputClass}
            placeholder="Jane"
            type="text"
            onChange={(e) => updateAnnonymousUser('firstName', e.target.value)}
          />
          {fieldError(annonymousUserValidDetails.firstName)}
        </div>
        <div>
          <label className={authLabelClass}>Last Name</label>
          <input
            className={authInputClass}
            placeholder="Doe"
            type="text"
            onChange={(e) => updateAnnonymousUser('lastName', e.target.value)}
          />
          {fieldError(annonymousUserValidDetails.lastName)}
        </div>
      </div>

      {/* Email */}
      <div className="mb-[22px]">
        <label className={authLabelClass}>Email Address</label>
        <input
          className={authInputClass}
          placeholder="you@example.com"
          type="text"
          onChange={(e) => {
            updateAnnonymousUser('emailAddress', e.target.value);
            dispatch(setEmail(e.target.value));
          }}
        />
        {fieldError(annonymousUserValidDetails.emailAddress)}
      </div>

      {/* Phone Number */}
      <div>
        <label className={authLabelClass}>Phone Number</label>
        <PhoneInput
          defaultCountry="NG"
          countries={['NG']}
          international
          countryCallingCodeEditable={false}
          limitMaxLength
          value={phone}
          onChange={(value) => setPhone(value || '')}
          numberInputProps={{
            name: 'phoneNumber',
            placeholder: '+234 000-0000',
            className: authInputClass,
          }}
        />
        {fieldError(annonymousUserValidDetails.phoneNumber)}
      </div>

      {/* Sign Up Button */}
      {auth_status !== 'loading' ? (
        <button
          className={`${authButtonClass} mt-[28px] ${
            validDetailsState
              ? 'bg-[#1E6DB4] hover:bg-[#195d9a] active:bg-[#154f83] text-white cursor-pointer'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
          }`}
          onClick={handleSignup}
        >
          Create Account
        </button>
      ) : (
        <div className="flex justify-center items-center gap-2 h-[52px] mt-[28px]">
          <img src={loadingGif} className="w-[32px] h-[20px] object-cover" />
          <span className="font-dmsans text-[13px] text-gray-500">
            Loading...
          </span>
        </div>
      )}

      {/* Login link */}
      <div className="mt-[24px] text-center font-dmsans text-[14px] text-gray-500">
        Already have an account?{' '}
        <span
          className="text-[#1E6DB4] cursor-pointer hover:underline font-medium"
          onClick={() => {
            navigate(`${routesPrefix}auth`);
          }}
        >
          Log in
        </span>
      </div>
    </AuthCard>
  );
}
